import { rateInputValidation } from './currency-rate-calculator'
import { formatNumber } from 'utils/format-number'
import { debounce } from 'utils/functions'
import { MarketBrief } from 'models/market'
import { serviceInfoPopup } from './handle-actual-offers'
import { getLangFromUrl } from 'utils/current-language'

declare let SINGLE_RATE_BASE_CURRENCY: string
declare let SINGLE_RATE_COUNTER_CURRENCY: string

interface RateSizeInterface {
  lower: string
  higher: string
}

interface StaticRateInterface {
  forexRate: number
}

export interface FormPayload {
  currency_pair: string
  my_price: string
  email: string
  layout: string
  current_price_must_be: string
  language: string
}

type RateSizeType = 'lower' | 'higher'

const pageLanguage: string = getLangFromUrl()
const currentCurrencyPair = `${SINGLE_RATE_BASE_CURRENCY}_${SINGLE_RATE_COUNTER_CURRENCY}`

const getLangOfCurrentPage = (): RateSizeInterface => {
  switch (pageLanguage) {
    case 'en':
      return {
        lower: 'lower',
        higher: 'higher',
      }

    default:
      return {
        lower: 'niższy',
        higher: 'wyższy',
      }
  }
}

export const payloadToSend: FormPayload = {
  currency_pair: currentCurrencyPair,
  my_price: '',
  email: '',
  layout: 'WT',
  current_price_must_be: 'HIGHER',
  language: pageLanguage,
}

export const staticRate: StaticRateInterface = {
  forexRate: null,
}

const setInteractiveElements = (value: RateSizeType): void => {
  const $popupCheckboxes = document.querySelectorAll<HTMLInputElement>('[data-radio-button]')
  $popupCheckboxes.forEach((checkbox) => {
    checkbox.dataset.radioButton === value ? (checkbox.checked = true) : (checkbox.checked = false)
  })

  const uiPhrases = getLangOfCurrentPage()

  const $popupDirectionSpan = document.querySelectorAll<HTMLSpanElement>('[data-rate-notification-direction]')
  $popupDirectionSpan.forEach((span) => {
    span.textContent = `${value === 'lower' ? uiPhrases['lower'] : uiPhrases['higher']}`
  })

  payloadToSend.current_price_must_be = value.toUpperCase()
}

const setWidthOfRangeElement = (percentageWidth: number, inputValue: string): void => {
  const $rangeElement = document.querySelector<HTMLElement>('[data-range-direction]')
  $rangeElement.style.left = `${percentageWidth}%`
  staticRate.forexRate > Number(inputValue)
    ? $rangeElement.classList.remove('is-right')
    : $rangeElement.classList.add('is-right')
}

const calculateTrackerPosition = (sliderCurrentValue: number): number => {
  const element = document.querySelector<HTMLInputElement>('[data-rate-notification-slider]')
  let percentage: number =
    (sliderCurrentValue - parseFloat(element.min)) / (parseFloat(element.min) - parseFloat(element.max))
  percentage += 0.5
  percentage *= 100
  percentage *= -1
  return Math.round(percentage)
}

const updateSliderRange = (forexRate: number): void => {
  const $rangeInput = document.querySelector<HTMLInputElement>('[data-rate-notification-slider]')
  if ($rangeInput) {
    $rangeInput.min = formatNumber(forexRate - 0.05, 4).replace(',', '.')
    $rangeInput.max = formatNumber(forexRate + 0.05, 4).replace(',', '.')
  }
}

const updateSliderAverageRate = (forexRate: number): void => {
  const $popupAverageRateHolder = document.querySelector<HTMLSpanElement>('[data-popup-average-rate]')

  if ($popupAverageRateHolder) {
    $popupAverageRateHolder.textContent = formatNumber(forexRate, 4)
    updateSliderRange(forexRate)
  }
}

const updatePopupsRate = (rate: string | number): void => {
  const $rateHolders = document.querySelectorAll<HTMLSpanElement>('[data-popup-rate-value]')
  const formatedRate = typeof rate === 'number' ? formatNumber(rate, 4) : rate

  if (Number(formatedRate.replace(',', '.')) < 0.01) {
    return
  }

  if ($rateHolders) {
    $rateHolders.forEach((holder) => {
      holder.textContent = formatedRate
    })
  }

  payloadToSend.my_price = formatedRate.replace(',', '.')

  if (staticRate.forexRate !== null) {
    Number(formatedRate.replace(',', '.')) > staticRate.forexRate
      ? setInteractiveElements('higher')
      : setInteractiveElements('lower')
  }
}

const setSliderValue = (value: string): void => {
  const $rateSliderInput = document.querySelector<HTMLInputElement>('[data-rate-notification-slider]')
  if ($rateSliderInput) {
    $rateSliderInput.value = value.replace(',', '.')
    setWidthOfRangeElement(calculateTrackerPosition(Number($rateSliderInput.value)), $rateSliderInput.value)
  }
}

const getCorrectValue = (value: string): string => {
  if (value.length < 6 && value.length > 2) while (value.length < 6) value += '0'
  if (value.length <= 2 && value.length > 0) {
    value.includes(',') ? (value += '0000') : (value += ',0000')
  }
  setSliderValue(value)
  updatePopupsRate(value)
  return value
}

const initCloseSliderPopupService = (): void => {
  const $sliderPopup = document.querySelector<HTMLDivElement>('[data-slider-holder]')
  const $notificationRateInput = document.querySelector<HTMLInputElement>('[data-notification-average-rate]')
  const $checkboxLabel = document.querySelectorAll<HTMLLabelElement>('.currency-rate-notification__radio-label')
  const $checkboxes = document.querySelectorAll<HTMLLabelElement>('[data-radio-button]')

  window.addEventListener('click', (e) => {
    const target = e?.target as HTMLElement
    const isClosest = target.closest('.currency-rate-notification__popup-holder')
    if (
      e.target !== $notificationRateInput &&
      !isClosest &&
      e.target !== $sliderPopup &&
      e.target !== $checkboxLabel[0] &&
      e.target !== $checkboxLabel[1] &&
      e.target !== $checkboxes[0] &&
      e.target !== $checkboxes[1]
    ) {
      $sliderPopup.hidden = true
    }
  })
}

const toggleWarning = (warningState) => {
  const element = document.querySelector<HTMLSpanElement>('[data-rate-notification-warning]')
  if (element) {
    element.hidden = !warningState
  }
}

const shoudShowWarning = (inputValue: number) => {
  if (inputValue < staticRate.forexRate * 0.97 || inputValue > staticRate.forexRate * 1.03) {
    toggleWarning(true)
  } else {
    toggleWarning(false)
  }
}

const initInputsService = (): void => {
  const $rateSliderInput = document.querySelector<HTMLInputElement>('[data-rate-notification-slider]')
  const $rateInput = document.querySelector<HTMLInputElement>('[data-notification-average-rate]')
  const $sliderPopup = document.querySelector<HTMLDivElement>('[data-slider-holder]')

  if ($rateSliderInput) {
    $rateSliderInput.addEventListener('input', () => {
      const formatedValue = $rateSliderInput.value.replace('.', ',')
      shoudShowWarning(Number($rateSliderInput.value))
      $rateInput.value = formatedValue
      setWidthOfRangeElement(calculateTrackerPosition(Number($rateSliderInput.value)), $rateSliderInput.value)
      updatePopupsRate(formatedValue)
    })
  }

  if ($rateInput) {
    $rateInput.addEventListener('click', () => {
      $sliderPopup.hidden = false
    })

    $rateInput.addEventListener('blur', () => {
      const inputValue = rateInputValidation($rateInput.value, 4)
      const correctValue = getCorrectValue(inputValue)
      $rateInput.value = correctValue
    })

    $rateInput.addEventListener('input', () => {
      const inputValue = rateInputValidation($rateInput.value, 4)
      shoudShowWarning(Number(inputValue.replace(',', '.')))
      $rateInput.value = inputValue
      debounce(() => {
        $rateInput.value = getCorrectValue(inputValue)
      }, 1000)
    })
  }
}

const radioButtonsService = (currentForexRate: number): void => {
  const $higherRateButton = document.querySelector<HTMLInputElement>('[data-radio-button="higher"]')
  const $lowerRateButton = document.querySelector<HTMLInputElement>('[data-radio-button="lower"]')
  const $notificationRateInput = document.querySelector<HTMLInputElement>('[data-notification-average-rate]')

  $higherRateButton.addEventListener('change', (e) => {
    const target = e.target as HTMLInputElement
    const calculatedHighValue = formatNumber(currentForexRate * 1.005, 4)
    target.checked ? setSliderValue(calculatedHighValue) : ''
    $notificationRateInput.value = calculatedHighValue
    updatePopupsRate(calculatedHighValue)
  })

  $lowerRateButton.addEventListener('change', (e) => {
    const target = e.target as HTMLInputElement
    const calculatedLowValue = formatNumber(currentForexRate * 0.995, 4)
    target.checked ? setSliderValue(calculatedLowValue) : ''
    $notificationRateInput.value = calculatedLowValue
    updatePopupsRate(calculatedLowValue)
  })
}

const initHelpTip = (): void => {
  const $signHolder = document.querySelector<HTMLSpanElement>('.currency-rate-notification__sign-holder')
  const $infoPopup = document.querySelector<HTMLSpanElement>('.currency-rate-notification__answer-info')

  serviceInfoPopup($signHolder, $infoPopup)
}

let isFirstRun = true
export function handleFeaturedForexRate(payload: MarketBrief): void {
  const forexRate = payload.bestOffers.forex_now
  updateSliderAverageRate(forexRate)

  if (isFirstRun) {
    document.querySelector<HTMLInputElement>('[data-notification-average-rate]').value = formatNumber(forexRate, 4)
    radioButtonsService(forexRate)
    setSliderValue(formatNumber(forexRate, 4))
    staticRate.forexRate = forexRate
    updatePopupsRate(forexRate)
    isFirstRun = false
  }
}

const initNotificationScrollButton = () => {
  const $calculatorNotificationButton = document.querySelector<HTMLAnchorElement>('[data-alert-button]')
  const $notificationElement = document.querySelector<HTMLDivElement>('.currency-rate-notification')
  if ($calculatorNotificationButton) {
    $calculatorNotificationButton.addEventListener('click', () => {
      $notificationElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
    })
  }
}

export const initNotoficationService = (): void => {
  initInputsService()
  initCloseSliderPopupService()
  initHelpTip()
  initNotificationScrollButton()
}
